import React, { useState, useEffect } from 'react';
import { API_ORIGIN } from '../config';
import {
  Container, Card, CardContent, Typography, TextField,
  Button, Grid, Alert, CircularProgress, Box, Divider,
  InputAdornment, IconButton, Switch, FormControlLabel, Paper, Chip
} from '@mui/material';
import { Save, Edit, Cancel, Visibility, VisibilityOff, Lock, Public, Security } from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = API_ORIGIN;

const AlumniProfilePage = () => {
  const { user } = useAuth();

  const [alumniData, setAlumniData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Privacy settings
  const [shareWithEmployers, setShareWithEmployers] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Editable fields
  const [formData, setFormData] = useState({
    'Email Contact': '',
    'Contact Number': '',
    'Alternative Phone Number': '',
    'Address': '',
    'Country': '',
    'Current Job Title': '',
    'Other Degree Attained': ''
  });

  // Password fields
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user?.email) {
      loadAlumniData();
      loadPrivacySettings();
    }
  }, [user]);

  const loadAlumniData = async () => {
    try {
      setLoading(true);
      console.log('Loading alumni data for:', user.email);

      const token = Cookies.get('idToken');
      const response = await axios.get(`${API_BASE}/api/alumni/profile/${user.email}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = response.data;
      setAlumniData(data);

      setFormData({
        'Email Contact': data['Email Contact'] || '',
        'Contact Number': data['Contact Number'] || '',
        'Alternative Phone Number': data['Alternative Phone Number'] || '',
        'Address': data['Address'] || '',
        'Country': data['Country'] || '',
        'Current Job Title': data['Current Job Title'] || '',
        'Other Degree Attained': data['Other Degree Attained'] || ''
      });

      setError('');
    } catch (err) {
      console.error('Error loading alumni data:', err);
      setError(err.response?.data?.error || 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const loadPrivacySettings = async () => {
    try {
      const token = Cookies.get('idToken');
      const response = await axios.get(`${API_BASE}/api/profile/privacy-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('[PRIVACY] Loaded settings:', response.data);
      setShareWithEmployers(response.data.shareWithEmployers);
    } catch (err) {
      console.error('[PRIVACY] Error loading settings:', err);
      // Don't show error for privacy settings, just log it
    }
  };

  const handlePrivacyToggle = async () => {
    setPrivacyLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('idToken');
      const newValue = !shareWithEmployers;

      await axios.put(`${API_BASE}/api/profile/privacy-settings`,
        { shareWithEmployers: newValue },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setShareWithEmployers(newValue);
      setSuccess(`Privacy updated! Your profile is now ${newValue ? 'visible to employers' : 'private'}.`);

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('[PRIVACY] Error updating:', err);
      setError('Failed to update privacy settings');
    } finally {
      setPrivacyLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordData({
      ...passwordData,
      [field]: e.target.value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('idToken');
      await axios.put(`${API_BASE}/api/alumni/${user.email}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Profile updated successfully!');
      setEditMode(false);
      loadAlumniData();

    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    setError('');
    setSuccess('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('All password fields are required');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setSaving(true);

    try {
      const token = Cookies.get('idToken');
      await axios.post(`${API_BASE}/api/auth/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess('Password changed successfully!');
      setPasswordMode(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      'Email Contact': alumniData['Email Contact'] || '',
      'Contact Number': alumniData['Contact Number'] || '',
      'Alternative Phone Number': alumniData['Alternative Phone Number'] || '',
      'Address': alumniData['Address'] || '',
      'Country': alumniData['Country'] || '',
      'Current Job Title': alumniData['Current Job Title'] || '',
      'Other Degree Attained': alumniData['Other Degree Attained'] || ''
    });
    setEditMode(false);
    setError('');
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordMode(false);
    setError('');
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading profile...
        </Typography>
      </Container>
    );
  }

  if (error && !alumniData) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#046A38' }}>
        My Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Privacy Settings Card */}
      <Card elevation={3} sx={{ mb: 3, border: '2px solid #FCCA18' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {shareWithEmployers ? (
              <Public color="primary" sx={{ fontSize: 40, mr: 2 }} />
            ) : (
              <Lock color="action" sx={{ fontSize: 40, mr: 2 }} />
            )}
            <Box flex={1}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Privacy Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Control who can see your alumni profile
              </Typography>
            </Box>
            <Chip
              label={shareWithEmployers ? 'Public' : 'Private'}
              color={shareWithEmployers ? 'success' : 'default'}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={shareWithEmployers}
                onChange={handlePrivacyToggle}
                disabled={privacyLoading}
                color="primary"
                size="large"
              />
            }
            label={
              <Typography variant="body1" fontWeight="bold">
                {shareWithEmployers
                  ? "My profile is visible to employers"
                  : "My profile is private"}
              </Typography>
            }
          />

          {privacyLoading && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: shareWithEmployers ? 'success.lighter' : 'grey.100' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Public color={shareWithEmployers ? 'success' : 'action'} fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Visible to Employers
                  </Typography>
                </Box>
                <Typography variant="caption" display="block">
                  • Name, Degree, School<br/>
                  • Graduation Year<br/>
                  • Contact Email & Phone<br/>
                  • Current Job Title
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Security color="action" fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Always Private
                  </Typography>
                </Box>
                <Typography variant="caption" display="block">
                  • Student ID<br/>
                  • Home Address<br/>
                  • USC Email<br/>
                  • Password
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Read-Only Information */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Alumni Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {!editMode && !passwordMode && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Lock />}
                    onClick={() => setPasswordMode(true)}
                    sx={{ borderColor: '#046A38', color: '#046A38' }}
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => setEditMode(true)}
                    sx={{ bgcolor: '#046A38', '&:hover': { bgcolor: '#035028' } }}
                  >
                    Edit Contact Info
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Full Name</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {alumniData?.['FULL NAME'] || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Student ID</Typography>
              <Typography variant="body1">{alumniData?.['Student Id'] || 'N/A'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Alumni ID</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#046A38' }}>
                {alumniData?.['AlumniID'] || 'Pending Assignment'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Degree</Typography>
              <Typography variant="body1">{alumniData?.['Degree Obtained'] || 'N/A'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">School</Typography>
              <Typography variant="body1">{alumniData?.['School'] || 'N/A'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Graduation Year</Typography>
              <Typography variant="body1">{alumniData?.['graduation'] || 'N/A'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Honors</Typography>
              <Typography variant="body1">{alumniData?.['Honors'] || 'None'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Change Password Section */}
      {passwordMode && (
        <Card elevation={3} sx={{ mb: 3, border: '2px solid #046A38' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              🔒 Change Password
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  helperText="Minimum 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handlePasswordCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handlePasswordSave}
                disabled={saving}
                sx={{ bgcolor: '#FCCA18', color: '#046A38', '&:hover': { bgcolor: '#E6B515' } }}
              >
                {saving ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Editable Contact Information */}
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            Contact Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData['Email Contact']}
                onChange={handleChange('Email Contact')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData['Contact Number']}
                onChange={handleChange('Contact Number')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Alternative Phone"
                value={formData['Alternative Phone Number']}
                onChange={handleChange('Alternative Phone Number')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData['Country']}
                onChange={handleChange('Country')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData['Address']}
                onChange={handleChange('Address')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Job Title"
                value={formData['Current Job Title']}
                onChange={handleChange('Current Job Title')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Other Degrees Attained"
                value={formData['Other Degree Attained']}
                onChange={handleChange('Other Degree Attained')}
                disabled={!editMode}
                variant={editMode ? 'outlined' : 'filled'}
                multiline
                rows={2}
                helperText="List any additional degrees or certifications"
              />
            </Grid>
          </Grid>

          {editMode && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{ bgcolor: '#FCCA18', color: '#046A38', '&:hover': { bgcolor: '#E6B515' } }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AlumniProfilePage;